import { Constants } from "@eagerdog/constants";
import { IRunningOrder, IRunningOrderDogClass, IShow, IShowRing } from "@eagerdog/interfaces";

import { GAP_BREAK_NAME, isBreak, isGapBreak } from "../timeline/labels";

export interface ScoreJudgeEffect {
  scoreIds?: string[];
  filter?: Record<string, unknown>;
  judge_name: string;
  judge_number: string;
}

export interface ShowElementJudgeEffect {
  show_element: string;
  level?: string;
  section?: string;
  judge_name: string;
  judge_number: string;
}

export interface ClassMovePlan {
  ok: true;
  runningOrder: IRunningOrder[];
  effects: { scores?: ScoreJudgeEffect; element?: ShowElementJudgeEffect };
}

export interface ClassMoveRefusal {
  ok: false;
  reason: "break" | "junior" | "missing";
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function tileKey(dogClass: IRunningOrderDogClass): string {
  const tile = dogClass as IRunningOrderDogClass & { start_letter?: string; end_letter?: string };
  return [
    tile.show_element, tile.level, tile.section, tile.sex_split, tile.variation,
    tile.split_number, tile.start_letter, tile.end_letter,
  ].map((value) => value ?? "").join("|");
}

export function nextRingName(show: IShow): string {
  return nextRingNameForShows([show]);
}

export function nextRingNameForShows(shows: IShow[]): string {
  const numbers = shows.flatMap((show) => (show.rings || []).map((ring) => {
    const match = ring.ring.match(/Ring (\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }));
  const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  return `Ring ${next}`;
}

export function judgeHasAssignments(judge: IRunningOrder): boolean {
  return (judge.dog_classes || []).some((dogClass) => {
    if (dogClass.type === Constants.running_order_dog_class_type.break) {
      return false;
    }
    return (dogClass.entries || 0) > 0 || (dogClass.dog_run_order || []).length > 0;
  });
}

export function replaceRingOrder(show: IShow, ringName: string, runningOrder: IRunningOrder[]): IShow {
  const next = clone(show);
  next.rings = (next.rings || []).map((ring) => ring.ring === ringName ? { ...ring, running_order: runningOrder } : ring);
  return next;
}

export function addBreakTile(
  runningOrder: IRunningOrder[],
  judgeIndex: number,
  atIndex?: number,
  details?: { name?: string; duration?: number }
): IRunningOrder[] {
  const order = clone(runningOrder);
  const judge = order[judgeIndex];
  if (!judge) {
    return order;
  }
  const duration = details?.duration && details.duration > 0 ? details.duration : 60;
  const tile: IRunningOrderDogClass = {
    type: Constants.running_order_dog_class_type.break,
    duration,
    total_minutes: duration,
    name: details?.name?.trim() || "Break",
  };
  if (atIndex == null || atIndex >= judge.dog_classes.length) {
    judge.dog_classes.push(tile);
  } else {
    judge.dog_classes.splice(Math.max(0, atIndex), 0, tile);
  }
  return order;
}

export function updateBreakTile(
  runningOrder: IRunningOrder[],
  judgeIndex: number,
  classIndex: number,
  patch: { name?: string; duration?: number }
): IRunningOrder[] {
  const order = clone(runningOrder);
  const tile = order[judgeIndex]?.dog_classes[classIndex];
  if (!tile) {
    return order;
  }
  if (patch.name != null) {
    tile.name = patch.name.trim() || "Break";
  }
  if (patch.duration != null) {
    tile.duration = patch.duration;
    tile.total_minutes = patch.duration;
  }
  return order;
}

export function removeTile(runningOrder: IRunningOrder[], judgeIndex: number, classIndex: number): IRunningOrder[] {
  const order = clone(runningOrder);
  order[judgeIndex]?.dog_classes.splice(classIndex, 1);
  return order;
}

export function reverseRing(runningOrder: IRunningOrder[]): IRunningOrder[] {
  return clone(runningOrder).map((judge) => ({
    ...judge,
    dog_classes: [...(judge.dog_classes || [])].reverse(),
  }));
}

export function resizeClassMinutes(dogClass: IRunningOrderDogClass, totalMinutes: number): number | undefined {
  const entries = dogClass.entries || 0;
  if (!entries) {
    return undefined;
  }
  return Math.round((totalMinutes / entries) * 10) / 10;
}

export function snapBreakMinutes(minutes: number): number {
  return Math.max(5, Math.round(minutes / 5) * 5);
}

/** Shortest a class bar can be: time per entry × number of entries. */
export function classLengthFloor(perEntry: number, entries: number): number {
  return Math.round(Math.max(0, perEntry) * Math.max(0, entries) * 10) / 10;
}

/**
 * Move one tile earlier or later without letting it overlap the previous tile.
 * A later move inserts or grows a hidden gap in front of the tile, which pushes
 * everything after it. An earlier move only consumes that gap.
 */
/**
 * Move a whole judge later or earlier. A later move inserts or grows a hidden gap
 * in front of the judge's first class, which pushes that judge and every judge after
 * them. An earlier move only consumes that gap, so the judge cannot overlap the one before.
 */
export function shiftJudgeByMinutes(
  runningOrder: IRunningOrder[],
  judgeIndex: number,
  deltaMinutes: number
): IRunningOrder[] | undefined {
  const classes = runningOrder[judgeIndex]?.dog_classes || [];
  const firstReal = classes.findIndex((dogClass) => !isGapBreak(dogClass));
  if (firstReal < 0) {
    return undefined;
  }
  return shiftTileByMinutes(runningOrder, judgeIndex, firstReal, deltaMinutes);
}

export function shiftTileByMinutes(
  runningOrder: IRunningOrder[],
  judgeIndex: number,
  classIndex: number,
  deltaMinutes: number
): IRunningOrder[] | undefined {
  if (!Number.isFinite(deltaMinutes) || Math.abs(deltaMinutes) < 0.25) {
    return undefined;
  }
  const order = clone(runningOrder);
  const classes = order[judgeIndex]?.dog_classes;
  if (!classes || !classes[classIndex]) {
    return undefined;
  }

  const growGapBefore = (minutes: number) => {
    const previous = classes[classIndex - 1];
    if (previous && isGapBreak(previous)) {
      previous.duration = (previous.duration || 0) + minutes;
      previous.total_minutes = previous.duration;
      return;
    }
    classes.splice(classIndex, 0, {
      type: Constants.running_order_dog_class_type.break,
      duration: minutes,
      total_minutes: minutes,
      name: GAP_BREAK_NAME,
    });
  };

  if (deltaMinutes > 0) {
    growGapBefore(Math.round(deltaMinutes * 10) / 10);
    return order;
  }

  const previous = classes[classIndex - 1];
  const need = Math.round(-deltaMinutes * 10) / 10;
  if (!previous || !isGapBreak(previous) || (previous.duration || 0) + 0.05 < need) {
    return undefined;
  }
  previous.duration = Math.round(((previous.duration || 0) - need) * 10) / 10;
  previous.total_minutes = previous.duration;
  if ((previous.duration || 0) <= 0.05) {
    classes.splice(classIndex - 1, 1);
  }
  return order;
}

/** Set a bar's length. Classes keep their per-entry duration; the bar just gets longer or shorter down to `floor`. */
export function setTileLength(
  runningOrder: IRunningOrder[],
  judgeIndex: number,
  classIndex: number,
  minutes: number,
  floor: number
): IRunningOrder[] | undefined {
  if (minutes + 0.05 < floor) {
    return undefined;
  }
  const order = clone(runningOrder);
  const tile = order[judgeIndex]?.dog_classes[classIndex];
  if (!tile) {
    return undefined;
  }
  const rounded = Math.round(minutes * 10) / 10;
  if (isBreak(tile)) {
    tile.duration = rounded;
    tile.total_minutes = rounded;
  } else {
    tile.total_minutes = rounded;
  }
  return order;
}

export function planClassMove(
  runningOrder: IRunningOrder[],
  sourceJudgeIndex: number,
  sourceClassIndex: number,
  destJudgeIndex: number,
  destInsertIndex: number,
  showId: string
): ClassMovePlan | ClassMoveRefusal {
  const source = runningOrder[sourceJudgeIndex];
  const dest = runningOrder[destJudgeIndex];
  const tile = source?.dog_classes[sourceClassIndex];
  if (!source || !dest || !tile) {
    return { ok: false, reason: "missing" };
  }
  if (sourceJudgeIndex !== destJudgeIndex) {
    if (tile.type === Constants.running_order_dog_class_type.break) {
      return { ok: false, reason: "break" };
    }
    if (tile.show_element === Constants.dog_class_element.junior_showmanship) {
      return { ok: false, reason: "junior" };
    }
  }

  const order = clone(runningOrder);
  const [moved] = order[sourceJudgeIndex].dog_classes.splice(sourceClassIndex, 1);
  let insertAt = destInsertIndex;
  if (sourceJudgeIndex === destJudgeIndex && sourceClassIndex < insertAt) {
    insertAt -= 1;
  }
  insertAt = Math.max(0, Math.min(insertAt, order[destJudgeIndex].dog_classes.length));
  order[destJudgeIndex].dog_classes.splice(insertAt, 0, moved);

  if (sourceJudgeIndex === destJudgeIndex) {
    return { ok: true, runningOrder: order, effects: {} };
  }

  const dupeIndex = order[destJudgeIndex].dog_classes.findIndex((dogClass) =>
    dogClass !== moved &&
    dogClass.show_element === moved.show_element &&
    dogClass.level === moved.level &&
    (dogClass.entries || 0) === 0
  );
  if (dupeIndex !== -1) {
    order[destJudgeIndex].dog_classes.splice(dupeIndex, 1);
  }

  const isSplit = moved.split_number !== undefined;
  const effects: ClassMovePlan["effects"] = {};
  const scoreIds = moved.dog_run_order || [];
  if (scoreIds.length > 0) {
    effects.scores = {
      scoreIds,
      judge_name: dest.judge_name,
      judge_number: dest.judge_number || "",
    };
  } else if (!isSplit && (moved.level || moved.show_element)) {
    const filter: Record<string, unknown> = {
      show_id: showId,
      judge_name: source.judge_name,
    };
    if (moved.show_element) filter["dog_class.show_element"] = moved.show_element;
    if (moved.level) filter["dog_class.level"] = moved.level;
    if (moved.section) filter["dog_class.section"] = moved.section;
    effects.scores = {
      filter,
      judge_name: dest.judge_name,
      judge_number: dest.judge_number || "",
    };
  }
  if (!isSplit && (moved.show_element || moved.level)) {
    effects.element = {
      show_element: moved.show_element || "",
      level: moved.level,
      section: moved.section,
      judge_name: dest.judge_name,
      judge_number: dest.judge_number || "",
    };
  }
  return { ok: true, runningOrder: order, effects };
}

function sectionEq(a?: string, b?: string): boolean {
  return (a || "") === (b || "");
}

export function splitClassTile(runningOrder: IRunningOrder[], judgeIndex: number, classIndex: number): { order: IRunningOrder[]; error?: string } {
  const order = clone(runningOrder);
  const tile = order[judgeIndex]?.dog_classes[classIndex];
  if (!tile || tile.type === Constants.running_order_dog_class_type.break) {
    return { order: runningOrder, error: "missing" };
  }
  if (tile.group_with) {
    return { order: runningOrder, error: "Ungroup this class before splitting." };
  }
  const currentSplitTotal = tile.split_total ?? 1;
  if (currentSplitTotal >= 4) {
    return { order: runningOrder, error: "Maximum of 4 splits reached." };
  }
  const newSplitTotal = currentSplitTotal + 1;
  order.forEach((judge) => {
    judge.dog_classes.forEach((dogClass) => {
      if (
        dogClass.show_element === tile.show_element &&
        dogClass.level === tile.level &&
        sectionEq(dogClass.section, tile.section) &&
        (dogClass.split_number !== undefined || dogClass === tile)
      ) {
        dogClass.split_total = newSplitTotal;
        if (dogClass.split_number === undefined) {
          dogClass.split_number = 1;
        }
      }
    });
  });

  const originalEntries = tile.entries ?? 0;
  const originalMins = tile.total_minutes;
  const durationPerEntry = tile.duration ?? (originalMins && originalEntries ? originalMins / originalEntries : undefined);
  const minsForCount = (count: number): number | undefined => {
    if (!count) return 0;
    if (durationPerEntry) return Math.round(count * durationPerEntry * 10) / 10;
    if (originalMins && originalEntries) return Math.round((originalMins * count / originalEntries) * 10) / 10;
    return undefined;
  };

  const existingOrder = tile.dog_run_order ?? [];
  if (existingOrder.length > 0) {
    const split1: string[] = [];
    const split2: string[] = [];
    existingOrder.forEach((id, index) => (index % 2 === 0 ? split1 : split2).push(id));
    const split1Count = split1.length || Math.ceil(originalEntries / 2);
    const split2Count = split2.length || Math.floor(originalEntries / 2);
    tile.dog_run_order = split1;
    tile.entries = split1Count;
    tile.total_minutes = minsForCount(split1Count) ?? tile.total_minutes;
    const created = clone(tile);
    created.split_number = newSplitTotal;
    created.dog_run_order = split2;
    created.entries = split2Count;
    created.total_minutes = minsForCount(split2Count) ?? tile.total_minutes;
    order[judgeIndex].dog_classes.splice(classIndex + 1, 0, created);
  } else {
    const split1Entries = Math.ceil(originalEntries / 2);
    const split2Entries = Math.floor(originalEntries / 2);
    tile.entries = split1Entries;
    tile.total_minutes = minsForCount(split1Entries) ?? tile.total_minutes;
    const created = clone(tile);
    created.split_number = newSplitTotal;
    created.dog_run_order = [];
    created.entries = split2Entries;
    created.total_minutes = minsForCount(split2Entries) ?? tile.total_minutes;
    order[judgeIndex].dog_classes.splice(classIndex + 1, 0, created);
  }
  return { order };
}

export function mergeClassSplits(runningOrder: IRunningOrder[], judgeIndex: number, classIndex: number): IRunningOrder[] {
  const order = clone(runningOrder);
  const tile = order[judgeIndex]?.dog_classes[classIndex];
  if (!tile || tile.split_number === undefined) {
    return runningOrder;
  }
  const isSibling = (dogClass: IRunningOrderDogClass) =>
    dogClass.show_element === tile.show_element &&
    dogClass.level === tile.level &&
    sectionEq(dogClass.section, tile.section) &&
    dogClass.split_number !== undefined;

  const allSplits: Array<{ judge: IRunningOrder; index: number }> = [];
  order.forEach((judge) => {
    judge.dog_classes.forEach((dogClass, index) => {
      if (isSibling(dogClass)) {
        allSplits.push({ judge, index });
      }
    });
  });
  allSplits.sort((a, b) => (a.judge.dog_classes[a.index].split_number || 0) - (b.judge.dog_classes[b.index].split_number || 0));
  if (allSplits.length < 2) {
    return runningOrder;
  }

  const anchor = allSplits[0].judge.dog_classes[allSplits[0].index];
  const pooled = [...(anchor.dog_run_order ?? [])];
  let totalEntries = anchor.entries ?? 0;
  let totalMinutes = anchor.total_minutes ?? 0;
  for (let i = 1; i < allSplits.length; i++) {
    const sibling = allSplits[i].judge.dog_classes[allSplits[i].index];
    pooled.push(...(sibling.dog_run_order ?? []));
    totalEntries += sibling.entries ?? 0;
    totalMinutes += sibling.total_minutes ?? 0;
  }
  anchor.dog_run_order = pooled;
  anchor.entries = totalEntries;
  anchor.total_minutes = totalMinutes;

  const removals = new Map<IRunningOrder, number[]>();
  for (let i = 1; i < allSplits.length; i++) {
    const list = removals.get(allSplits[i].judge) || [];
    list.push(allSplits[i].index);
    removals.set(allSplits[i].judge, list);
  }
  removals.forEach((indices, judge) => {
    indices.sort((a, b) => b - a).forEach((index) => judge.dog_classes.splice(index, 1));
  });
  delete anchor.split_number;
  delete anchor.split_total;
  return order;
}

export function groupClasses(
  runningOrder: IRunningOrder[],
  judgeIndex: number,
  classIndexes: number[]
): { order: IRunningOrder[]; error?: string } {
  if (classIndexes.length < 2) {
    return { order: runningOrder, error: "Please select at least 2 levels to group together" };
  }
  const order = clone(runningOrder);
  const classes = order[judgeIndex]?.dog_classes;
  if (!classes) {
    return { order: runningOrder, error: "missing" };
  }
  const [parentIndex, ...childIndexes] = classIndexes;
  const parent = classes[parentIndex];
  if (!parent || parent.type === Constants.running_order_dog_class_type.break) {
    return { order: runningOrder, error: "Select class tiles, not breaks." };
  }
  childIndexes.forEach((index, orderIndex) => {
    const child = classes[index];
    if (!child) return;
    child.group_with = `${parent.show_element}:${parent.level}`;
    child.group_order = orderIndex;
    parent.entries = (parent.entries || 0) + (child.entries || 0);
    parent.total_minutes = (parent.total_minutes || 0) + (child.total_minutes || 0);
  });
  const childNames = childIndexes.map((index) => classes[index]?.level).filter(Boolean);
  (parent as IRunningOrderDogClass & { label?: string }).label = [parent.level, ...childNames].filter(Boolean).join("/");
  return { order };
}

export function ungroupClass(runningOrder: IRunningOrder[], judgeIndex: number, classIndex: number): IRunningOrder[] {
  const order = clone(runningOrder);
  const classes = order[judgeIndex]?.dog_classes;
  const parent = classes?.[classIndex];
  if (!classes || !parent) {
    return runningOrder;
  }
  const parentNew = `${parent.show_element}:${parent.level}`;
  const children = classes.filter((dogClass) =>
    dogClass.group_with === parentNew || (dogClass.group_with === parent.level && dogClass.show_element === parent.show_element)
  );
  children.forEach((child) => {
    parent.entries = Math.max(0, (parent.entries || 0) - (child.entries || 0));
    parent.total_minutes = Math.max(0, (parent.total_minutes || 0) - (child.total_minutes || 0));
    delete child.group_with;
    delete child.group_order;
    delete (child as IRunningOrderDogClass & { label?: string }).label;
  });
  delete (parent as IRunningOrderDogClass & { label?: string }).label;
  if (parent.group_with) {
    delete parent.group_with;
    delete parent.group_order;
  }
  return order;
}

export function deleteJudgeTile(runningOrder: IRunningOrder[], judgeIndex: number): { order: IRunningOrder[]; error?: string } {
  const judge = runningOrder[judgeIndex];
  if (!judge) {
    return { order: runningOrder, error: "missing" };
  }
  if (judgeHasAssignments(judge)) {
    return { order: runningOrder, error: "This judge still has classes assigned. To swap in a different judge, use Edit Judge on their block — the classes, entries and class setup move with the new judge. Otherwise remove their classes first." };
  }
  const order = clone(runningOrder);
  order.splice(judgeIndex, 1);
  return { order };
}

export function moveJudgeToRing(show: IShow, fromRing: string, judgeIndex: number, toRing: string): { show: IShow; rings: string[]; error?: string } {
  const next = clone(show);
  const source = (next.rings || []).find((ring) => ring.ring === fromRing);
  const target = (next.rings || []).find((ring) => ring.ring === toRing);
  if (!source || !target || fromRing === toRing) {
    return { show, rings: [], error: "missing" };
  }
  if (!source.running_order || !source.running_order[judgeIndex]) {
    return { show, rings: [], error: "missing" };
  }
  if (!target.running_order) {
    target.running_order = [];
  }
  const [judge] = source.running_order.splice(judgeIndex, 1);
  const existing = target.running_order.find((ro) =>
    ro.judge_name === judge.judge_name && (ro.judge_number || "") === (judge.judge_number || "")
  );
  if (existing) {
    const existingKeys = new Set((existing.dog_classes || []).filter((dc) => !isBreak(dc)).map(tileKey));
    existing.dog_classes = [
      ...(existing.dog_classes || []),
      ...(judge.dog_classes || []).filter((dc) => isBreak(dc) || !existingKeys.has(tileKey(dc))),
    ];
  } else {
    target.running_order.push(judge);
  }
  return { show: next, rings: [fromRing, toRing] };
}

/** Move a class between rings. Same-ring moves should use planClassMove. */
export function transferClassToRing(
  sourceOrder: IRunningOrder[],
  sourceJudgeIndex: number,
  sourceClassIndex: number,
  destOrder: IRunningOrder[],
  destJudgeIndex: number,
  destInsertIndex: number,
  showId: string
): { source: IRunningOrder[]; dest: IRunningOrder[]; effects: ClassMovePlan["effects"] } | ClassMoveRefusal {
  const sourceJudge = sourceOrder[sourceJudgeIndex];
  const tile = sourceJudge?.dog_classes[sourceClassIndex];
  const destJudge = destOrder[destJudgeIndex];
  if (!sourceJudge || !tile || !destJudge) {
    return { ok: false, reason: "missing" };
  }
  if (tile.type === Constants.running_order_dog_class_type.break) {
    return { ok: false, reason: "break" };
  }
  if (tile.show_element === Constants.dog_class_element.junior_showmanship) {
    return { ok: false, reason: "junior" };
  }

  const source = clone(sourceOrder);
  const dest = clone(destOrder);
  const [moved] = source[sourceJudgeIndex].dog_classes.splice(sourceClassIndex, 1);
  // A copy of this exact tile already in the destination ring would schedule it twice.
  const movedKey = tileKey(moved);
  const isCopy = (dc: IRunningOrderDogClass) => !isBreak(dc) && tileKey(dc) === movedKey;
  const copiesBeforeInsert = dest[destJudgeIndex].dog_classes.slice(0, destInsertIndex).filter(isCopy).length;
  dest.forEach((ro) => {
    ro.dog_classes = ro.dog_classes.filter((dc) => !isCopy(dc));
  });
  const insertAt = Math.max(0, Math.min(destInsertIndex - copiesBeforeInsert, dest[destJudgeIndex].dog_classes.length));
  dest[destJudgeIndex].dog_classes.splice(insertAt, 0, moved);
  const dupeIndex = dest[destJudgeIndex].dog_classes.findIndex((dogClass) =>
    dogClass !== moved &&
    dogClass.show_element === moved.show_element &&
    dogClass.level === moved.level &&
    (dogClass.entries || 0) === 0
  );
  if (dupeIndex !== -1) {
    dest[destJudgeIndex].dog_classes.splice(dupeIndex, 1);
  }

  const effects: ClassMovePlan["effects"] = {};
  const scoreIds = moved.dog_run_order || [];
  const isSplit = moved.split_number !== undefined;
  if (scoreIds.length > 0) {
    effects.scores = { scoreIds, judge_name: destJudge.judge_name, judge_number: destJudge.judge_number || "" };
  } else if (!isSplit && (moved.level || moved.show_element)) {
    const filter: Record<string, unknown> = { show_id: showId, judge_name: sourceJudge.judge_name };
    if (moved.show_element) filter["dog_class.show_element"] = moved.show_element;
    if (moved.level) filter["dog_class.level"] = moved.level;
    if (moved.section) filter["dog_class.section"] = moved.section;
    effects.scores = { filter, judge_name: destJudge.judge_name, judge_number: destJudge.judge_number || "" };
  }
  if (!isSplit && (moved.show_element || moved.level)) {
    effects.element = {
      show_element: moved.show_element || "",
      level: moved.level,
      section: moved.section,
      judge_name: destJudge.judge_name,
      judge_number: destJudge.judge_number || "",
    };
  }
  return { source, dest, effects };
}

export function ringByName(show: IShow, ringName: string): IShowRing | undefined {
  return (show.rings || []).find((ring) => ring.ring === ringName);
}
